// Travel Partners SSO Integration Script
document.addEventListener("DOMContentLoaded", function () {
  injectSpinnerStyles();

  // Small delay to ensure DOM is fully loaded
  setTimeout(() => {
    const buttons = document.querySelectorAll('.travel-button');
    buttons.forEach(button => {
      button.addEventListener("click", handleTravelClick);
    });
  }, 500);
});

// --- Retry window config ---
// Their side publishes the fresh session_id a few seconds after login, so after
// a click we keep re-checking for it for up to RETRY_WINDOW_MS, RETRY_INTERVAL_MS
// apart, before giving up.
const RETRY_WINDOW_MS = 10000;
const RETRY_INTERVAL_MS = 700;

// =============================
// Get sessionId from window variable
// =============================
function getSessionIdFromMeta() {
  if (window.sessionId) {
    const sessionId = window.sessionId;
    if (sessionId && sessionId.trim() !== '') {
      return sessionId;
    }
  }
  return null;
}

// =============================
// Get membership number from window variable
// =============================
function getMembershipNumberFromMeta() {
  if (window.customerMembershipNumber) {
    const membershipNumber = window.customerMembershipNumber;
    if (membershipNumber && membershipNumber.trim() !== '') {
      return membershipNumber;
    }
  }
  return null;
}

// =============================
// Spinner helpers
// =============================
function injectSpinnerStyles() {
  if (document.getElementById('sso-spinner-styles')) return;
  const styles = `
    <style id="sso-spinner-styles">
      .sso-loading-overlay {
        position: fixed; top: 0; left: 0; width: 100%; height: 100%;
        background: rgba(0, 0, 0, 0.5); display: flex; justify-content: center; align-items: center;
        z-index: 9999; opacity: 0; visibility: hidden;
        transition: opacity 0.3s ease, visibility 0.3s ease;
      }
      .sso-loading-overlay.show { opacity: 1; visibility: visible; }
      .sso-spinner-container { background: white; padding: 30px; border-radius: 10px;
        text-align: center; box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15); max-width: 300px; }
      .sso-spinner { width: 40px; height: 40px; border: 4px solid #f3f3f3; border-top: 4px solid #007bff;
        border-radius: 50%; animation: sso-spin 1s linear infinite; margin: 0 auto 15px; }
      @keyframes sso-spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
      .sso-loading-text { color: #333; font-size: 16px; margin: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
    </style>
  `;
  document.head.insertAdjacentHTML('beforeend', styles);
}

function showLoadingSpinner(message = "Redirecting to Travel Partners...") {
  let overlay = document.getElementById('sso-loading-overlay');
  if (!overlay) {
    const spinnerHTML = `
      <div id="sso-loading-overlay" class="sso-loading-overlay">
        <div class="sso-spinner-container">
          <div class="sso-spinner"></div>
          <p class="sso-loading-text">${message}</p>
        </div>
      </div>
    `;
    document.body.insertAdjacentHTML('beforeend', spinnerHTML);
    overlay = document.getElementById('sso-loading-overlay');
    setTimeout(() => overlay.classList.add('show'), 10);
  } else {
    const textEl = overlay.querySelector('.sso-loading-text');
    if (textEl) textEl.textContent = message;
    overlay.classList.add('show');
  }
}

function hideLoadingSpinner() {
  const overlay = document.getElementById('sso-loading-overlay');
  if (overlay) {
    overlay.classList.remove('show');
    setTimeout(() => overlay.remove(), 300);
  }
}

// =============================
// WebView detection
// =============================
function isWebView() {
  const ua = navigator.userAgent || navigator.vendor || window.opera;

  const isIOSWebView = /iP(ad|hone|od)/.test(ua) && !/(Safari|CriOS|FxiOS|EdgiOS)/i.test(ua);
  const isAndroidWebView = /Android/.test(ua) && (/wv|Version\/\d+\.\d+/.test(ua) && !/Chrome\/[.0-9]*/.test(ua) || /; wv\)/.test(ua));

  const hasWebViewIndicators =
    (window.ReactNativeWebView !== undefined) ||
    (window.flutter_inappwebview !== undefined) ||
    (window.webkit?.messageHandlers !== undefined) ||
    (typeof AndroidInterface !== 'undefined');

  return isIOSWebView || isAndroidWebView || hasWebViewIndicators;
}

// =============================
// Click handler
// =============================
function handleTravelClick(event) {
  // Logged-out visitors must never enter the SSO chain (no session can exist —
  // they got the spinner + "Session not ready" alert; Dirk, PROD, 4 Sep).
  // Send them to the travel desk page to log in themselves. mmLoggedIn is
  // server-rendered truth from layout/theme.liquid.
  if (window.mmLoggedIn !== true) {
    event.preventDefault();
    window.location.href = "https://www.multiply.co.za/engaged/flyoverTravelDesk";
    return;
  }

  event.preventDefault();

  showLoadingSpinner("Logging you in...");

  // Two timing problems are handled by the orchestrator below:
  //  1) DELAYED METAFIELD: their side publishes the fresh session_id a few SECONDS
  //     after login, so the baked-in window.sessionId can be behind. Each attempt
  //     re-fetches a fresh server render and reads the CURRENT session_id.
  //  2) GATEWAY WARM-UP: the first singleSignOn per session can 403 before the
  //     freshly-minted token propagates.
  // So we keep re-checking for up to ~5s instead of giving up after one try.
  runSsoWithRetry(Date.now() + RETRY_WINDOW_MS);
}

// =============================
// Fetch FRESH credentials
// Re-renders the current page server-side (logged-in context, cache-busted) and
// reads the CURRENT session_id / membership_number from it — bypassing stale
// values baked into the initial post-login page load. Never rejects: falls back
// to the page globals if the request or parse fails.
// =============================
function fetchFreshCredentials() {
  const path = window.location.pathname + window.location.search;
  const url = path + (path.indexOf('?') === -1 ? '?' : '&') + '_ssofresh=' + Date.now();

  return fetch(url, { credentials: 'include', headers: { 'X-Requested-With': 'fetch' } })
    .then(res => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.text();
    })
    .then(html => {
      const sidMatch = html.match(/window\.sessionId\s*=\s*'([^']*)'/);
      const memMatch = html.match(/window\.customerMembershipNumber\s*=\s*([^;]+);/);

      let clientNumber = null;
      if (memMatch) {
        try {
          clientNumber = JSON.parse(memMatch[1].trim());
        } catch (e) {
          clientNumber = memMatch[1].trim().replace(/^['"]|['"]$/g, '');
        }
      }

      return {
        sessionId: sidMatch ? sidMatch[1].trim() : null,
        clientNumber: clientNumber == null ? null : String(clientNumber).trim()
      };
    })
    .catch(() => ({ sessionId: null, clientNumber: null }));
}

// =============================
// SSO orchestration (fresh credentials + warm-up retry)
// Each attempt pulls FRESH credentials, then runs the auth-token -> SSO chain.
// Retries cover both the stale-metafield window and the gateway warm-up 403.
// =============================
function runSsoWithRetry(deadline) {
  return fetchFreshCredentials()
    .then(fresh => {
      const sessionId = (fresh.sessionId && fresh.sessionId.trim()) || getSessionIdFromMeta();
      const clientNumber = (fresh.clientNumber && String(fresh.clientNumber).trim()) || getMembershipNumberFromMeta();

      if (!sessionId) throw new Error('NO_SESSION');
      if (!clientNumber) throw new Error('NO_MEMBERSHIP');

      return getAuthToken(sessionId).then(authToken => ssoLogin(clientNumber, authToken));
    })
    .then(redirectUrl => {
      hideLoadingSpinner();
      openRedirect(redirectUrl);
    })
    .catch(err => {
      // Keep re-checking until the retry window closes. A missing/stale session_id
      // (their side hasn't published the fresh one yet) and the gateway's first-call
      // 403 are both transient — each retry re-fetches fresh credentials, so we just
      // keep trying until the deadline.
      if (Date.now() < deadline) {
        return new Promise(resolve => setTimeout(resolve, RETRY_INTERVAL_MS))
          .then(() => runSsoWithRetry(deadline));
      }

      hideLoadingSpinner();
      if (err && (err.message === 'NO_SESSION' || err.message === 'NO_MEMBERSHIP')) {
        alert("⚠️ Session not ready. Please refresh the page and try again.");
      } else {
        alert("⚠️ Could not log in, please try again.");
      }
      throw err;
    });
}

// Opens the travel desk. Same-tab inside webviews; a new tab on the web, with a
// same-tab fallback if the popup is blocked (the retry path can open outside the
// original click gesture).
function openRedirect(redirectUrl) {
  if (isWebView()) {
    window.location.href = redirectUrl;
    return;
  }
  const newWindow = window.open(redirectUrl, "_blank");
  if (!newWindow) {
    window.location.href = redirectUrl;
  }
}

// =============================
// API helpers
// =============================
function getAuthToken(sessionId) {
  if (!sessionId) {
    return Promise.reject("Session ID not found.");
  }

  const authUrl = "https://multiply-mall-backend.multiply.co.za/api/Multipass/AccessToken?sessionId=" + encodeURIComponent(sessionId);

  return fetch(authUrl)
    .then(res => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.text();
    })
    .then(text => {
      if (!text) throw new Error("Empty response");
      return text;
    });
}

function ssoLogin(clientNumber, authToken) {
  const ssoUrl = "https://webprd.multiply.co.za/multiply-api-gateway/partner/v1/multiply/travel/singleSignOn";

  return fetch(ssoUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${authToken}`
    },
    body: JSON.stringify({ clientNumber })
  })
    .then(res => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    })
    .then(data => {
      if (!data.RedirectURL) {
        throw new Error(data.ErrorMsg || "Login failed");
      }
      return data.RedirectURL;
    });
}

// =============================
// Cleanup on back navigation
// =============================
function cleanupOnReturn() {
  const overlay = document.getElementById('sso-loading-overlay');
  if (overlay) {
    overlay.remove();
  }

  const nativeSpinnerSelectors = [
    '.loading-spinner',
    '.native-spinner',
    '.webview-loading',
    '[class*="spinner"]',
    '[class*="loading"]',
    '[id*="spinner"]',
    '[id*="loading"]'
  ];

  nativeSpinnerSelectors.forEach(selector => {
    document.querySelectorAll(selector).forEach(el => {
      const style = window.getComputedStyle(el);
      if (style.position === 'fixed' || style.position === 'absolute') {
        if (el.offsetWidth < 200 && el.offsetHeight < 200) {
          el.remove();
        }
      }
    });
  });
}

window.addEventListener('pageshow', cleanupOnReturn);

document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') {
    cleanupOnReturn();
  }
});
